import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { mapCrmError } from './crm-api.errors';
import { CRM_API_URL } from './crm-api.tokens';
import {
  FaqDto,
  FaqFilterSelects,
  FaqListQuery,
  FaqListResult,
  FaqNamedOption,
  FaqRequest,
} from './faqs-api.types';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 10;
const FAQS_PATH = '/customer-service/faqs';

@Injectable({ providedIn: 'root' })
export class FaqsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(CRM_API_URL).replace(/\/+$/, '')}${FAQS_PATH}`;

  list(query: FaqListQuery = {}): Observable<FaqListResult> {
    return this.http.get<unknown>(this.baseUrl, { params: this.listParams(query) }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => this.retryDelay(error) }),
      map(body => unwrapList(body)),
      catchError(error => throwError(() => mapCrmError(error))),
    );
  }

  filterSelects(): Promise<FaqFilterSelects> {
    return this.read(`${this.baseUrl}/filter-selects`, unwrapFilterSelects);
  }

  formSelects(): Promise<FaqNamedOption[]> {
    return this.read(`${this.baseUrl}/form-selects`, unwrapFormSelects);
  }

  getById(uuid: string): Promise<FaqDto> {
    return this.mutate(this.http.get<unknown>(`${this.baseUrl}/${uuid}`), unwrapItem);
  }

  create(payload: FaqRequest): Promise<FaqDto> {
    return this.mutate(this.http.post<unknown>(this.baseUrl, payload), unwrapItem);
  }

  update(uuid: string, payload: FaqRequest): Promise<FaqDto> {
    return this.mutate(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapItem,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.mutate(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.mutate(this.http.delete<unknown>(`${this.baseUrl}/${uuid}`), () => undefined);
  }

  private read<T>(url: string, project: (body: unknown) => T): Promise<T> {
    return firstValueFrom(
      this.http.get<unknown>(url).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => this.retryDelay(error) }),
        map(project),
        catchError(error => throwError(() => mapCrmError(error))),
      ),
    );
  }

  private async mutate<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapCrmError(error);
    }
  }

  private listParams(query: FaqListQuery): HttpParams {
    let params = new HttpParams()
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));

    if (query.question) params = params.set('filters[question]', query.question);
    if (query.answer) params = params.set('filters[answer]', query.answer);
    if (query.category?.length) {
      params = params.set('select[category]', query.category.join(','));
    }
    if (query.status?.length) {
      params = params.set('select[status]', query.status.join(','));
    }
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.order) params = params.set('order', query.order);

    return params;
  }

  private retryDelay(error: unknown): Observable<number> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        const retryAfter = Number(error.headers.get('Retry-After'));
        const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
        return timer(seconds * 1000);
      }
      if (error.status >= 500 || error.status === 0) {
        return timer(300);
      }
    }
    return throwError(() => error);
  }
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): FaqListResult {
  const data = unwrapData(body);
  if (data && typeof data === 'object') {
    const page = data as {
      data?: unknown;
      pagination?: { totalRecords?: unknown };
    };
    if (Array.isArray(page.data)) {
      const totalRecords = page.pagination?.totalRecords;
      return {
        items: page.data.map(normalizeItem),
        total: typeof totalRecords === 'number' ? totalRecords : page.data.length,
      };
    }
  }
  return { items: [], total: 0 };
}

function unwrapItem(body: unknown): FaqDto {
  return normalizeItem(unwrapData(body));
}

function unwrapFilterSelects(body: unknown): FaqFilterSelects {
  const data = unwrapData(body);
  if (!data || typeof data !== 'object') {
    return { status: [], categories: [] };
  }
  const record = data as { status?: unknown; categories?: unknown };
  return {
    status: namedOptions(record.status),
    categories: namedOptions(record.categories),
  };
}

function unwrapFormSelects(body: unknown): FaqNamedOption[] {
  const data = unwrapData(body);
  if (!data || typeof data !== 'object' || !('categories' in data)) return [];
  return namedOptions((data as { categories?: unknown }).categories);
}

function namedOptions(value: unknown): FaqNamedOption[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const option = item as { uuid?: unknown; name?: unknown };
    if (typeof option.uuid !== 'string' || typeof option.name !== 'string') return [];
    return [{ uuid: option.uuid, name: option.name }];
  });
}

function normalizeItem(value: unknown): FaqDto {
  const item = (value ?? {}) as {
    uuid?: unknown;
    question?: unknown;
    answer?: unknown;
    category?: { uuid?: unknown; name?: unknown };
    status?: { name?: unknown; value?: unknown; tone?: unknown };
  };
  return {
    uuid: typeof item.uuid === 'string' ? item.uuid : '',
    question: typeof item.question === 'string' ? item.question : '',
    answer: typeof item.answer === 'string' ? item.answer : '',
    category: {
      uuid: typeof item.category?.uuid === 'string' ? item.category.uuid : '',
      name: typeof item.category?.name === 'string' ? item.category.name : '',
    },
    status: {
      name: typeof item.status?.name === 'string' ? item.status.name : '',
      value: typeof item.status?.value === 'string' ? item.status.value : '',
      tone: typeof item.status?.tone === 'string' ? item.status.tone : '',
    },
  };
}
